// Drop-in replacement for the Amplify `Auth` and `onAuthUIStateChange` surface.
// Authentication is delegated to WorkOS AuthKit (hosted UI). On sign-in the
// browser redirects to WorkOS, which redirects back to /auth/callback with a
// code. bootstrapAuth() detects that URL and exchanges the code automatically.

import config from "./config";

const TOKEN_KEY = "dr.session.token";
const REFRESH_TOKEN_KEY = "dr.session.refresh_token";
const USER_KEY = "dr.session.user";
const STATE_KEY = "workos_auth_state";

export const AuthState = Object.freeze({
  Loading: "loading",
  SignedOut: "signedout",
  SignedIn: "signedIn",
  // Legacy values kept for any remaining call-sites; not actively set.
  SignIn: "signin",
  SignUp: "signup",
  ConfirmSignUp: "confirmSignUp",
});

const subscribers = new Set();
let currentState = AuthState.Loading;
let currentUser = null;

function emit() {
  for (const fn of subscribers) {
    try {
      fn(currentState, currentUser);
    } catch (err) {
      console.error("auth subscriber error", err);
    }
  }
}

function setState(next, user) {
  currentState = next;
  currentUser = user || null;
  if (user) {
    try {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch {}
  } else {
    try {
      localStorage.removeItem(USER_KEY);
    } catch {}
  }
  emit();
}

function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getAuthToken() {
  return getStoredToken();
}

function setStoredToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

function getStoredRefreshToken() {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

function setStoredRefreshToken(token) {
  try {
    if (token) localStorage.setItem(REFRESH_TOKEN_KEY, token);
    else localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch {}
}

function decodeJwtPayload(token) {
  try {
    const part = token.split(".")[1];
    const padded = part.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (part.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

async function callAuth(path, body, method = "POST") {
  const headers = { "content-type": "application/json" };
  const token = getStoredToken();
  if (token) headers.authorization = `Bearer ${token}`;
  const response = await fetch(`${config.apiBaseUrl}/auth/${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || `Auth request failed (${response.status})`);
    error.code = data.code || `HTTP_${response.status}`;
    throw error;
  }
  return data;
}

function applySession({ user, token, refreshToken }) {
  if (token) setStoredToken(token);
  if (refreshToken) setStoredRefreshToken(refreshToken);
  setState(AuthState.SignedIn, user);
  return user;
}

async function tryRefresh() {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return false;
  try {
    const result = await callAuth("refresh", { refreshToken });
    if (result.token) {
      setStoredToken(result.token);
      if (result.refreshToken) setStoredRefreshToken(result.refreshToken);
      return true;
    }
  } catch {}
  return false;
}

export const Auth = {
  async currentAuthenticatedUser() {
    if (currentUser) return currentUser;
    const token = getStoredToken();
    if (!token) {
      const err = new Error("not authenticated");
      err.code = "NotAuthorizedException";
      throw err;
    }
    const result = await callAuth("me", null, "GET");
    if (!result.user) {
      setStoredToken(null);
      setStoredRefreshToken(null);
      setState(AuthState.SignedOut, null);
      const err = new Error("not authenticated");
      err.code = "NotAuthorizedException";
      throw err;
    }
    setState(AuthState.SignedIn, result.user);
    return result.user;
  },

  // Redirects the browser to the WorkOS AuthKit hosted sign-in/sign-up UI.
  async signIn() {
    const result = await callAuth("login", null, "GET");
    try { sessionStorage.setItem(STATE_KEY, result.state); } catch {}
    window.location.href = result.url;
  },

  async signUp() {
    const result = await callAuth("login?screen_hint=sign-up", null, "GET");
    try { sessionStorage.setItem(STATE_KEY, result.state); } catch {}
    window.location.href = result.url;
  },

  // Called by bootstrapAuth when it detects /auth/callback in the URL.
  async exchangeCode(code, returnedState) {
    let storedState;
    try { storedState = sessionStorage.getItem(STATE_KEY); } catch {}
    if (storedState && returnedState && storedState !== returnedState) {
      throw new Error("Authentication state mismatch — please try signing in again");
    }
    try { sessionStorage.removeItem(STATE_KEY); } catch {}
    const result = await callAuth("exchange", { code });
    return applySession(result);
  },

  async signOut() {
    try {
      await callAuth("signout", {});
    } catch (err) {
      console.warn("signOut request failed", err);
    }
    setStoredToken(null);
    setStoredRefreshToken(null);
    setState(AuthState.SignedOut, null);
  },

  async configure() {
    // Compatibility shim — configuration lives in src/api/config.js.
  },
};

export async function bootstrapAuth() {
  setState(AuthState.Loading, null);

  // Handle the WorkOS redirect callback transparently before anything else.
  if (typeof window !== "undefined" && window.location.pathname === "/auth/callback") {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    if (code) {
      try {
        await Auth.exchangeCode(code, state);
        // Replace the callback URL so the back button doesn't re-trigger the flow.
        window.history.replaceState(null, "", "/");
        return currentUser;
      } catch (err) {
        console.error("WorkOS callback failed", err);
        setState(AuthState.SignedOut, null);
        return null;
      }
    }
  }

  const token = getStoredToken();
  if (!token) {
    setState(AuthState.SignedOut, null);
    return null;
  }

  // WorkOS access tokens expire in ~1 hour. Proactively refresh if the token
  // is expired or within 5 minutes of expiry.
  const payload = decodeJwtPayload(token);
  const now = Math.floor(Date.now() / 1000);
  if (!payload || !payload.exp || payload.exp - now < 300) {
    const refreshed = await tryRefresh();
    if (!refreshed) {
      setStoredToken(null);
      setStoredRefreshToken(null);
      setState(AuthState.SignedOut, null);
      return null;
    }
  }

  try {
    const result = await callAuth("me", null, "GET");
    if (result.user) {
      setState(AuthState.SignedIn, result.user);
      return result.user;
    }
  } catch (err) {
    console.warn("bootstrapAuth failed", err);
  }
  setStoredToken(null);
  setStoredRefreshToken(null);
  setState(AuthState.SignedOut, null);
  return null;
}

export function onAuthUIStateChange(callback) {
  subscribers.add(callback);
  try {
    callback(currentState, currentUser);
  } catch (err) {
    console.error(err);
  }
  return () => subscribers.delete(callback);
}

export function getCurrentAuthState() {
  return { state: currentState, user: currentUser };
}
